import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { PageHeader, Card, StatusPill } from '@/components/Primitives';
import { Field } from './Extensions';
import { Plug, Sun, PaperPlaneTilt, Webhooks } from '@phosphor-icons/react';
import { useToast } from '@/hooks/use-toast';

const SolarGreen = () => {
    const [status, setStatus] = useState(null);
    const [webhookCfg, setWebhookCfg] = useState(null);
    const [test, setTest] = useState(null);
    const [testing, setTesting] = useState(false);
    const [pingResult, setPingResult] = useState(null);
    const [sgLeads, setSgLeads] = useState([]);
    const [webhookSecret, setWebhookSecret] = useState('');
    const [sgUser, setSgUser] = useState('');
    const [sgPwd, setSgPwd] = useState('');
    const { toast } = useToast();

    const load = async () => {
        const [s, t, wc] = await Promise.all([
            api.get('/integrations/solar-green/status'),
            api.get('/integrations/solar-green/test'),
            api.get('/integrations/solar-green/webhook-config'),
        ]);
        setStatus(s.data);
        setTest(t.data);
        setWebhookCfg(wc.data);
    };
    useEffect(() => { load(); }, []);

    const saveSSO = async () => {
        await api.put('/integrations/solar-green/status', {
            sso_enabled: status.sso_enabled,
            auto_provision_role: status.auto_provision_role,
        });
        toast({ title: 'SSO uloženo' });
    };

    const saveWebhook = async () => {
        try {
            const payload = {
                webhook_enabled: webhookCfg.webhook_enabled,
                webhook_url: webhookCfg.webhook_url,
                auto_create_leads: webhookCfg.auto_create_leads,
                lead_intents: webhookCfg.lead_intents,
            };
            if (webhookSecret) payload.webhook_secret = webhookSecret;
            if (sgUser) payload.sg_username = sgUser;
            if (sgPwd) payload.sg_password = sgPwd;
            await api.put('/integrations/solar-green/webhook-config', payload);
            setWebhookSecret(''); setSgUser(''); setSgPwd('');
            load();
            toast({ title: 'Webhook uloženo' });
        } catch (e) {
            toast({ title: 'Chyba', description: e.response?.data?.detail || 'error', variant: 'destructive' });
        }
    };

    const pingWebhook = async () => {
        try {
            const r = await api.post('/integrations/solar-green/test-webhook');
            setPingResult(r.data);
            toast({
                title: r.data.forwarded ? 'Ping doručen' : 'Ping selhal',
                description: JSON.stringify(r.data.response || r.data.error || '').slice(0, 100),
                variant: r.data.forwarded ? 'default' : 'destructive',
            });
        } catch (e) {
            toast({ title: 'Chyba', variant: 'destructive' });
        }
    };

    const loadLeads = async () => {
        try {
            const r = await api.get('/integrations/solar-green/leads', { params: { limit: 10 } });
            setSgLeads(r.data.items || r.data || []);
        } catch (e) {
            toast({ title: 'Chyba načtení', description: e.response?.data?.detail || 'error', variant: 'destructive' });
        }
    };

    if (!status || !webhookCfg) return <div>Načítání…</div>;

    return (
        <>
            <PageHeader
                eyebrow="External CMS"
                title="Solar Green CMS"
                description="SSO most + obousměrný webhook s call lifecycle eventy + automatické zápis poptávek."
                actions={
                    <button onClick={async () => { setTesting(true); await load(); setTesting(false); }}
                            data-testid="sg-test-btn"
                            className="inline-flex items-center gap-2 px-3 py-2 text-xs font-bold uppercase tracking-[0.08em] border hover:bg-[var(--bg-surface-hover)]"
                            style={{ borderColor: 'var(--border-default)', borderRadius: 2 }}>
                        <Plug size={14} weight="bold" />
                        {testing ? 'Načítám…' : 'Refresh'}
                    </button>
                }
            />

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
                <Card title="Connectivity">
                    <div className="space-y-2 text-sm">
                        <div className="flex items-center gap-2">
                            <Sun size={20} color="#10B981" />
                            <span className="font-mono text-xs">{status.base_url}</span>
                        </div>
                        <StatusPill status={test?.ok ? 'success' : 'error'}
                                    label={test?.ok ? 'API ONLINE' : 'API NEDOSTUPNÉ'} />
                    </div>
                </Card>

                <Card title="SSO Bridge">
                    <label className="inline-flex items-center gap-2 text-sm mb-3">
                        <input type="checkbox" checked={!!status.sso_enabled}
                               data-testid="sg-sso-enabled"
                               onChange={(e) => setStatus({ ...status, sso_enabled: e.target.checked })} />
                        Povolit SSO
                    </label>
                    <div>
                        <label className="text-[10px] uppercase font-bold" style={{ color: 'var(--text-secondary)' }}>Auto-provision role</label>
                        <select value={status.auto_provision_role}
                                onChange={(e) => setStatus({ ...status, auto_provision_role: e.target.value })}
                                className="w-full mt-1 px-2 py-1.5 bg-[var(--bg-app)] border text-sm"
                                style={{ borderColor: 'var(--border-default)', borderRadius: 2 }}>
                            <option value="read_only">read_only</option>
                            <option value="voice_manager">voice_manager</option>
                            <option value="admin">admin</option>
                        </select>
                    </div>
                    <button onClick={saveSSO} className="mt-3 px-3 py-2 text-xs font-bold uppercase"
                            style={{ background: 'var(--brand)', color: '#fff', borderRadius: 2 }}>
                        Uložit SSO
                    </button>
                </Card>

                <Card title="Stav">
                    <div className="text-[10px] uppercase font-bold mb-1" style={{ color: 'var(--text-secondary)' }}>Posl. SSO login</div>
                    <div className="font-mono text-xs mb-3">{status.last_sso_login_at || 'Nikdy'}</div>
                    <div className="text-[10px] uppercase font-bold mb-1" style={{ color: 'var(--text-secondary)' }}>Webhook</div>
                    <StatusPill status={webhookCfg.webhook_enabled ? 'success' : 'neutral'}
                                label={webhookCfg.webhook_enabled ? 'aktivní' : 'vypnuto'} />
                </Card>
            </div>

            <Card title="Webhook → Solar Green (call lifecycle)" className="mb-6">
                <p className="text-xs mb-3" style={{ color: 'var(--text-secondary)' }}>
                    Po každém ukončeném hovoru Voice Gateway pošle event na <code>{webhookCfg.webhook_url}</code> s podpisem HMAC SHA-256
                    v hlavičce <code>X-Voice-Gateway-Signature</code>. Solar Green CMS volání rozpozná, spáruje s kontaktem a založí lead.
                </p>
                <div className="grid grid-cols-2 gap-3">
                    <Field label="Webhook URL" value={webhookCfg.webhook_url}
                           onChange={(v) => setWebhookCfg({ ...webhookCfg, webhook_url: v })} mono />
                    <Field label="HMAC secret (sdílený se Solar Green)" value={webhookSecret}
                           onChange={setWebhookSecret} type="password" mono />
                    <label className="inline-flex items-center gap-2 text-sm mt-6">
                        <input type="checkbox" checked={!!webhookCfg.webhook_enabled}
                               data-testid="sg-webhook-enabled"
                               onChange={(e) => setWebhookCfg({ ...webhookCfg, webhook_enabled: e.target.checked })} />
                        Aktivovat webhook
                    </label>
                    <label className="inline-flex items-center gap-2 text-sm mt-6">
                        <input type="checkbox" checked={!!webhookCfg.auto_create_leads}
                               onChange={(e) => setWebhookCfg({ ...webhookCfg, auto_create_leads: e.target.checked })} />
                        Auto-vytvořit leady při AI intent matchi
                    </label>
                </div>
                <div className="mt-4 grid grid-cols-3 gap-3 border-t pt-3" style={{ borderColor: 'var(--border-default)' }}>
                    <Field label="SG Username (pro čtení leadů)" value={sgUser} onChange={setSgUser} mono />
                    <Field label="SG Password" value={sgPwd} onChange={setSgPwd} type="password" mono />
                    <div className="text-[11px] mt-7" style={{ color: 'var(--text-disabled)' }}>
                        {webhookCfg.sg_api_credentials_set ? '✓ Credentials uloženy (zašifrované)' : 'Nejsou nastaveny'}
                    </div>
                </div>
                <div className="mt-4 flex gap-2">
                    <button onClick={saveWebhook} data-testid="sg-webhook-save-btn"
                            className="px-3 py-2 text-xs font-bold uppercase"
                            style={{ background: 'var(--brand)', color: '#fff', borderRadius: 2 }}>
                        Uložit webhook
                    </button>
                    <button onClick={pingWebhook} disabled={!webhookCfg.webhook_enabled}
                            data-testid="sg-webhook-ping-btn"
                            className="inline-flex items-center gap-2 px-3 py-2 text-xs font-bold uppercase border"
                            style={{ borderColor: 'var(--border-default)', borderRadius: 2 }}>
                        <PaperPlaneTilt size={14} weight="bold" />Test ping
                    </button>
                </div>
                {pingResult && (
                    <pre className="mt-3 px-3 py-2 font-mono text-[11px] overflow-x-auto"
                         style={{ background: '#06080A', borderRadius: 2 }}>{JSON.stringify(pingResult, null, 2)}</pre>
                )}
            </Card>

            <Card title="Solar Green Leads (live preview)" className="mb-6"
                  action={
                      <button onClick={loadLeads}
                              data-testid="sg-leads-refresh"
                              className="px-2 py-1 text-[10px] font-bold uppercase border"
                              style={{ borderColor: 'var(--border-default)', borderRadius: 2 }}>
                          Načíst posledních 10
                      </button>
                  }>
                {sgLeads.length === 0 ? (
                    <p className="text-xs text-center py-4" style={{ color: 'var(--text-secondary)' }}>
                        Klikněte „Načíst" pro zobrazení posledních leadů ze Solar Green.
                    </p>
                ) : (
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-[10px] uppercase tracking-[0.1em]" style={{ color: 'var(--text-secondary)' }}>
                                <th className="text-left py-2">ID</th>
                                <th className="text-left">Kategorie</th>
                                <th className="text-left">Zákazník</th>
                                <th className="text-left">Telefon</th>
                                <th className="text-right">Hodnota</th>
                            </tr>
                        </thead>
                        <tbody>
                            {sgLeads.map((l) => (
                                <tr key={l.submission_id || l.id} className="border-t" style={{ borderColor: 'var(--border-default)' }}>
                                    <td className="py-2 font-mono text-xs">{l.submission_id || l.id}</td>
                                    <td className="text-xs">{l.category_name || l.category || '—'}</td>
                                    <td className="text-xs">{l.customer?.name || l.name || '—'}</td>
                                    <td className="font-mono text-xs">{l.customer?.phone || l.phone || '—'}</td>
                                    <td className="text-right font-mono text-xs">
                                        {l.calculation?.total_with_vat
                                            ? `${l.calculation.total_with_vat.toFixed(2)} ${l.calculation.currency || ''}`
                                            : '—'}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </Card>
        </>
    );
};

export default SolarGreen;
